import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { JobHttpService } from 'cms/features/feature_job/service';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    Box,
    CircularProgress,
    FormControlLabel,
    Checkbox
} from '@mui/material';

function SalaryDetailForm({ open, onClose, salaryDetailId, salarySpecificationId, onSaved }) {
    const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [isTotalAmountFocused, setIsTotalAmountFocused] = useState(false);

    const isTaxable = watch('is_taxable');
    const isRecurring = watch('is_recurring');
    const quantity = watch('quantity');
    const unitPrice = watch('unit_price');

    useEffect(() => {
        if (open) {
            setIsTotalAmountFocused(false);
            if (salaryDetailId) {
                loadSalaryDetail();
            } else {
                reset({
                    description: '',
                    is_taxable: true,
                    is_recurring: true,
                    quantity: '',
                    unit_price: '',
                    total_amount: ''
                });
                setError('');
            }
        }
    }, [open, salaryDetailId, reset]);

    // Auto-calculate total amount when quantity or unit_price changes
    // Only calculate if total_amount field is not currently focused (user is not editing it)
    useEffect(() => {
        if (!isTotalAmountFocused && quantity && unitPrice) {
            const qty = parseFloat(quantity);
            const price = parseFloat(unitPrice);
            if (!isNaN(qty) && !isNaN(price)) {
                const calculatedTotal = qty * price;
                setValue('total_amount', calculatedTotal.toFixed(2), { shouldValidate: false });
            }
        }
    }, [quantity, unitPrice, isTotalAmountFocused, setValue]);

    const loadSalaryDetail = async () => {
        if (!salaryDetailId) return;
        
        setLoadingDetail(true);
        setError('');
        try {
            const detail = await JobHttpService.loadSalaryDetail(salaryDetailId);
            reset({
                description: detail.description || '',
                is_taxable: detail.is_taxable !== undefined ? detail.is_taxable : true,
                is_recurring: detail.is_recurring !== undefined ? detail.is_recurring : true,
                quantity: detail.quantity || '',
                unit_price: detail.unit_price || '',
                total_amount: detail.total_amount || ''
            });
        } catch (err) {
            console.error('Error loading salary detail:', err);
            setError(err?.message || 'Failed to load salary detail');
        } finally {
            setLoadingDetail(false);
        }
    };

    const onSubmit = async (data) => {
        setLoading(true);
        setError('');

        try {
            const submitData = {
                salary_specification: salarySpecificationId,
                description: data.description,
                is_taxable: data.is_taxable || false,
                is_recurring: data.is_recurring || false,
                quantity: data.quantity ? parseInt(data.quantity) : null,
                unit_price: data.unit_price ? parseFloat(data.unit_price) : null,
                total_amount: data.total_amount ? parseFloat(data.total_amount) : null
            };

            let response;
            if (salaryDetailId) {
                response = await JobHttpService.updateSalaryDetail(salaryDetailId, submitData);
            } else {
                response = await JobHttpService.createSalaryDetail(submitData);
            }

            if (response && !response.detail && !response.error) {
                if (onSaved) {
                    onSaved();
                }
                onClose();
            } else {
                setError(response?.detail || response?.error || 'Failed to save salary detail');
            }
        } catch (err) {
            console.error('Error saving salary detail:', err);
            setError(err?.message || 'Failed to save salary detail. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    {salaryDetailId ? 'Edit Salary Detail' : 'Add Salary Detail'}
                </DialogTitle>
                <DialogContent>
                    {loadingDetail && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {!loadingDetail && (
                        <>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        {...register("description", {
                                            required: "Description is required"
                                        })}
                                        error={Boolean(errors.description)}
                                        helperText={errors.description?.message}
                                        placeholder="e.g., Monthly Salary, Car Benefit"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...register("is_taxable")}
                                                defaultChecked={true}
                                            />
                                        }
                                        label="Is Taxable"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...register("is_recurring")}
                                                defaultChecked={true}
                                            />
                                        }
                                        label="Is Recurring"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Quantity"
                                        type="number"
                                        {...register("quantity")}
                                        error={Boolean(errors.quantity)}
                                        helperText={errors.quantity?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Unit Price"
                                        type="number"
                                        step="0.01"
                                        {...register("unit_price")}
                                        error={Boolean(errors.unit_price)}
                                        helperText={errors.unit_price?.message}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Total Amount"
                                        type="number"
                                        step="0.01"
                                        {...register("total_amount", {
                                            required: "Total amount is required"
                                        })}
                                        error={Boolean(errors.total_amount)}
                                        helperText={errors.total_amount?.message || "Auto-calculated from quantity × unit price (you can edit manually)"}
                                        onFocus={() => setIsTotalAmountFocused(true)}
                                        onBlur={() => setIsTotalAmountFocused(false)}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading || loadingDetail}
                    >
                        {loading ? <CircularProgress size={20} /> : (salaryDetailId ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default SalaryDetailForm;
